// Brand Risk data retention - how long mention data is kept before the
// nightly purge deletes it permanently.
//
// Write access is org_admin or higher; the backend returns HTTP 403 to
// anyone else, and this UI mirrors that gate rather than guessing.
// Shrinking the window is a two-step call: the first PUT comes back 409
// with the number of mentions that would be destroyed, and we re-send with
// confirm:true only after the admin agrees.
import React, { useEffect, useMemo, useState } from 'react';
import {
    bp_get_retention_settings, bp_put_retention_settings,
} from '../../../services';
import {
    formatLocalDateTime, formatNumber, unwrap, writeResult,
} from './_shared';
import CommonButton from '../../../Components/CommonButton';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonLoader from '../../../Components/CommonLoader';
import StatCard from '../../../Components/StatCard';
import StatusBadge from '../../../Components/StatusBadge';
import { useConfirm } from '../../../Components/ConfirmModal';
import { isOrgAdminOrHigher } from '../../../helper';
import { Body, SubHeading } from '../../../Constants/Fonts';
import ModuleIcon from '../../../Components/ModuleIcon';

const PRESET_LABELS = {
    0: 'Keep forever',
    1: '1 day',
    30: '30 days',
    45: '45 days',
    60: '60 days',
    90: '90 days',
    180: '180 days',
    365: '1 year',
};

function presetLabel({ days, minutes }) {
    if (minutes === 0) return PRESET_LABELS[0];
    if (minutes < 1440) {
        const hours = minutes / 60;
        return `${hours} hour${hours === 1 ? '' : 's'}`;
    }
    return PRESET_LABELS[days] || `${days} days`;
}

function Panel({ title, note, children }) {
    return (
        <section className="bm_ret_panel cmn_elevated_card">
            {(title || note) && (
                <div className="bm_ret_panel_head">
                    <div>
                        {title && (
                            <SubHeading as="h3" variant={2} className="bm_ret_panel_title">
                                {title}
                            </SubHeading>
                        )}
                        {note && (
                            <Body as="span" variant={2} className="bm_ret_muted">{note}</Body>
                        )}
                    </div>
                </div>
            )}
            {children}
        </section>
    );
}

export default function RetentionSection() {
    const canManage = isOrgAdminOrHigher();
    const { confirm, confirmModal } = useConfirm();
    const [cfg, setCfg] = useState(null);
    const [draft, setDraft] = useState(null);
    const [preview, setPreview] = useState(null);
    const [err, setErr] = useState(null);
    const [notice, setNotice] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => {
        bp_get_retention_settings().then(r => {
            const d = unwrap(r) || {};
            setCfg(d);
            setDraft(d.retention_minutes);
        }).catch(() => setErr('Could not load the retention policy.'));
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (cfg == null || draft == null || draft === cfg.retention_minutes) {
            setPreview(null);
            return;
        }
        let alive = true;
        bp_get_retention_settings({ preview_minutes: draft })
            .then(r => { if (alive) setPreview((unwrap(r) || {}).preview || null); })
            .catch(() => { if (alive) setPreview(null); });
        return () => { alive = false; };
    }, [draft, cfg]);

    const options = useMemo(() => {
        const seenMinutes = new Set();
        const allowed = (cfg?.available_presets || [])
            .filter(p => p.allowed && !seenMinutes.has(p.minutes) && seenMinutes.add(p.minutes))
            .map(p => ({ value: p.minutes, label: presetLabel(p) }));
        const current = cfg?.retention_minutes;
        if (current != null && !allowed.some(o => o.value === current)) {
            allowed.push({
                value: current,
                label: `${presetLabel({ days: cfg.retention_days, minutes: current })} (above plan limit)`,
            });
        }
        return allowed.sort((a, b) => a.value - b.value);
    }, [cfg]);

    const save = async (confirmed = false) => {
        setSaving(true);
        setErr(null);
        setNotice(null);
        const { httpStatus, body: r } = await writeResult(bp_put_retention_settings(
            confirmed ? { retention_minutes: draft, confirm: true }
                : { retention_minutes: draft },
        ));
        if (r.status === 1) {
            const pending = r.data?.pending_deletion || 0;
            setNotice(pending
                ? `Saved. ${formatNumber(pending)} mentions are now past the `
                  + 'window and will be deleted in tonight\'s purge.'
                : 'Saved.');
            setPreview(null);
            load();
            setSaving(false);
            return;
        }
        if ((httpStatus === 409 || r.requires_confirmation) && !confirmed) {
            setSaving(false);
            const ok = await confirm({
                title: 'Permanently delete older mentions?',
                message: r.message || 'This will permanently delete mentions '
                    + 'older than the new window. It cannot be undone.',
                confirmLabel: 'Delete and save',
                cancelLabel: 'Keep current window',
                danger: true,
            });
            if (ok) await save(true);
            return;
        }
        setErr(r.message || r.error
            || (httpStatus === 403
                ? 'Only organization admins can change the retention window.'
                : 'Save failed.'));
        setSaving(false);
    };

    if (!cfg) return (
        <div className="bm_ret_loading"><CommonLoader label="Loading retention policy..." /></div>
    );

    const dirty = draft !== cfg.retention_minutes;
    const effectiveMinutes = cfg.effective_retention_minutes;
    const deletedCount = cfg.last_purge_deleted_count || 0;
    const currentLabel = effectiveMinutes
        ? presetLabel({ days: cfg.effective_retention_days, minutes: effectiveMinutes })
        : 'Keep forever';

    return (
        <>
            {confirmModal}
            <div className="bm_ret">
                {err && (
                    <div className="bm_ret_alert bm_ret_alert--error" role="alert">
                        <StatusBadge variant="danger">Error</StatusBadge>
                        <Body as="span" variant={2}>{err}</Body>
                    </div>
                )}
                {notice && (
                    <div className="bm_ret_alert bm_ret_alert--success" role="status">
                        <StatusBadge variant="success">Saved</StatusBadge>
                        <Body as="span" variant={2}>{notice}</Body>
                    </div>
                )}

                <div className="bm_ret_kpis">
                    <StatCard className="cmn_elevated_card"
                        label="Retention window"
                        value={currentLabel}
                        hint={cfg.is_default ? 'No window set yet' : 'Effective window'}
                        icon={<span className="bm_kpi_icon"><ModuleIcon name="history" size={18} color="currentColor" /></span>} iconLeft />
                    <StatCard className="cmn_elevated_card"
                        label="Plan max"
                        value={cfg.plan_ceiling_days ? `${cfg.plan_ceiling_days}d` : 'No limit'}
                        hint="Maximum allowed by your plan"
                        icon={<span className="bm_kpi_icon is-neutral"><ModuleIcon name="compliance" size={18} color="currentColor" /></span>} iconLeft />
                    <StatCard className="cmn_elevated_card"
                        label="Last purge"
                        value={cfg.last_purge_at
                            ? new Date(cfg.last_purge_at).toLocaleDateString()
                            : 'Never'}
                        hint={cfg.last_purge_at ? formatLocalDateTime(cfg.last_purge_at) : 'No purge run yet'}
                        icon={<span className="bm_kpi_icon is-neutral"><ModuleIcon name="trash" size={18} color="currentColor" /></span>} iconLeft />
                    <StatCard className="cmn_elevated_card"
                        label="Deleted last purge"
                        value={formatNumber(deletedCount)}
                        hint="Mentions permanently removed"
                        icon={<span className={`bm_kpi_icon is-${deletedCount > 0 ? 'warning' : 'neutral'}`}><ModuleIcon name="risk" size={18} color="currentColor" /></span>} iconLeft />
                </div>

                <Panel title="Retention window">
                    <div className="bm_ret_field_row">
                        <CommonDropdown
                            ariaLabel="Retention window"
                            value={draft}
                            onChange={(v) => setDraft(Number(v))}
                            options={options}
                            disabled={!canManage}
                            minWidth={260}
                        />
                        <CommonButton
                            text={saving ? 'Saving...' : 'Save'}
                            variant="primary" className="bm_scan_btn"
                            onClick={() => save(false)}
                            disable={!canManage || saving || !dirty}
                        />
                    </div>

                    {preview && (
                        <div className="bm_ret_preview">
                            <Body as="span" variant={2}>
                                {preview.affected_mentions
                                    ? <>Switching to <strong>{options.find(o => o.value === draft)?.label || `${draft} min`}</strong>
                                        {' '}would permanently delete{' '}
                                        <strong>{formatNumber(preview.affected_mentions)}</strong> mentions
                                        posted before {formatLocalDateTime(preview.cutoff)}.</>
                                    : <>No stored mentions fall outside this window; nothing would be deleted.</>}
                            </Body>
                        </div>
                    )}

                    {!canManage && (
                        <Body as="p" variant={2} className="bm_ret_readonly">
                            Read-only. Only organization admins can change the retention window.
                        </Body>
                    )}

                    {cfg.updated_by && (
                        <Body as="p" variant={2} className="bm_ret_meta">
                            Last changed by {cfg.updated_by}
                        </Body>
                    )}
                </Panel>
            </div>
        </>
    );
}
